import React, { Component } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  FormFeedback,
  Alert,
} from 'reactstrap';
import { validatePassword, getPasswordRequirements } from '../../utils/passwordValidator';
import { updatePassword } from '../../actions/user';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import s from './ForcePasswordChangeModal.module.scss';

class ForcePasswordChangeModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      password: '',
      passwordAgain: '',
      errors: {},
      validationErrors: [],
      isSubmitting: false,
      showPassword: false,
      showPasswordAgain: false,
    };
  }

  handleInputChange = (e) => {
    const { name, value } = e.target;
    this.setState({ [name]: value }, () => {
      // Validate immediately when user types
      this.validatePassword();
    });
  };

  validatePassword = () => {
    const { password, passwordAgain } = this.state;
    const errors = {};
    const validationErrors = [];

    // Validate password format
    if (password) {
      const validation = validatePassword(password);
      if (!validation.isValid) {
        validationErrors.push(...validation.errors);
      }
    }

    // Check if passwords match - validate whenever either field has a value
    if (passwordAgain) {
      if (password) {
        // Both fields have values, check if they match
        if (password !== passwordAgain) {
          errors.passwordAgain = 'Mật khẩu xác nhận không khớp';
        }
        // If passwords match, don't set error (errors.passwordAgain will be undefined)
      } else {
        // User typed in confirm password but not new password
        errors.passwordAgain = 'Vui lòng nhập mật khẩu mới trước';
      }
    }
    // If passwordAgain is empty, errors.passwordAgain will be undefined (no error)

    this.setState({ errors, validationErrors });
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    const { password, passwordAgain } = this.state;
    const errors = {};

    if (!password) {
      errors.password = 'Vui lòng nhập mật khẩu mới';
    }
    if (!passwordAgain) {
      errors.passwordAgain = 'Vui lòng xác nhận mật khẩu';
    }

    // Validate password format
    const validation = validatePassword(password);
    if (!validation.isValid) {
      this.setState({ validationErrors: validation.errors, errors });
      return;
    }

    // Check if passwords match
    if (password && passwordAgain && password !== passwordAgain) {
      errors.passwordAgain = 'Mật khẩu xác nhận không khớp';
      this.setState({ errors });
      return;
    }

    this.setState({ isSubmitting: true, errors: {} });

    try {
      const token = localStorage.getItem('id_token');
      const jwt = require('jsonwebtoken');
      const decoded = jwt.decode(token);
      
      // For forced password change, use empty password (will be handled by backend)
      await this.props.dispatch(updatePassword({
        id: decoded.id,
        password: '', // Empty for forced password change
        passwordNew: password,
        passwordAgain: passwordAgain,
      }));

      // Close modal and reload page
      window.location.reload();
    } catch (error) {
      this.setState({
        isSubmitting: false,
        errors: { submit: error.message || 'Cập nhật mật khẩu thất bại' },
      });
    }
  };

  togglePasswordVisibility = (field) => {
    if (field === 'password') {
      this.setState({ showPassword: !this.state.showPassword });
    } else {
      this.setState({ showPasswordAgain: !this.state.showPasswordAgain });
    }
  };

  render() {
    const { isOpen } = this.props;
    const { password, passwordAgain, errors, validationErrors, isSubmitting, showPassword, showPasswordAgain } = this.state;
    const requirements = getPasswordRequirements();

    return (
      <Modal isOpen={isOpen} backdrop="static" keyboard={false} className={s.modal}>
        <ModalHeader className="text-danger">
          <strong>Yêu Cầu Thay Đổi Mật Khẩu</strong>
        </ModalHeader>
        <ModalBody>
          <Alert color="warning">
            <strong>Thông Báo Bảo Mật:</strong> Mật khẩu hiện tại của bạn không đáp ứng yêu cầu bảo mật. 
            Vui lòng thay đổi mật khẩu để tiếp tục sử dụng hệ thống.
          </Alert>

          <Form onSubmit={this.handleSubmit}>
            <FormGroup>
              <Label for="password">Mật Khẩu Mới</Label>
              <div className="position-relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={password}
                  onChange={this.handleInputChange}
                  invalid={!!errors.password || validationErrors.length > 0}
                  placeholder="Nhập mật khẩu mới"
                />
                <span className={s.passwordIcon} onClick={() => this.togglePasswordVisibility('password')}>
                  <i className={`fa fa-eye${showPassword ? "-slash" : ""}`} />
                </span>
              </div>
              <FormFeedback>{errors.password}</FormFeedback>
            </FormGroup>

            <FormGroup>
              <Label for="passwordAgain">Xác Nhận Mật Khẩu Mới</Label>
              <div className="position-relative">
                <Input
                  type={showPasswordAgain ? "text" : "password"}
                  id="passwordAgain"
                  name="passwordAgain"
                  value={passwordAgain}
                  onChange={this.handleInputChange}
                  invalid={!!errors.passwordAgain}
                  placeholder="Nhập lại mật khẩu mới"
                />
                <span className={s.passwordIcon} onClick={() => this.togglePasswordVisibility('passwordAgain')}>
                  <i className={`fa fa-eye${showPasswordAgain ? "-slash" : ""}`} />
                </span>
              </div>
              <FormFeedback>{errors.passwordAgain}</FormFeedback>
              {!errors.passwordAgain && passwordAgain && password && password === passwordAgain && (
                <div className="text-success small mt-1">
                  <i className="fa fa-check-circle" /> Mật khẩu khớp
                </div>
              )}
            </FormGroup>

            {validationErrors.length > 0 && (
              <Alert color="danger" className="mt-2">
                <strong>Yêu Cầu Mật Khẩu:</strong>
                <ul className="mb-0 mt-2">
                  {requirements.map((req, index) => {
                    const isMet = !validationErrors.some(err => 
                      err.toLowerCase().includes(req.toLowerCase().split(' ')[0])
                    );
                    return (
                      <li key={index} className={isMet ? 'text-success' : 'text-danger'}>
                        {isMet ? '✓' : '✗'} {req}
                      </li>
                    );
                  })}
                </ul>
              </Alert>
            )}

            {errors.submit && (
              <Alert color="danger" className="mt-2">
                {errors.submit}
              </Alert>
            )}

            <div className="d-flex justify-content-end mt-3">
              <Button color="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Đang cập nhật...' : 'Thay Đổi Mật Khẩu'}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>
    );
  }
}

ForcePasswordChangeModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default connect()(ForcePasswordChangeModal);

